import { useCallback, useMemo } from "react";
import type { ListViewStatus } from "@carbon6/c6-frontend-design";
import {
  getTrainingSubmitOptions,
  listDatasets,
  listMyDatasetUsage,
  listMyExperiments,
  listTeamDatasetUsage,
  listTeamExperiments,
  type TrainingDatasetUsage,
  type TrainingExperiment,
} from "@/api";
import { useAsyncList } from "./useAsyncList";
import { useTeamMembers } from "./useTeamMembers";
// 데이터셋 선택지로 받는 카탈로그 첫 페이지 크기. 검색은 이 목록 안에서 좁힌다.
const DATASET_OPTION_PAGE_SIZE = 100;

export type SessionFilterOption = { value: string; label: string; description?: string };

export type SessionFilterOptions = {
  memberOptions: readonly SessionFilterOption[];
  experimentOptions: readonly SessionFilterOption[];
  branchOptions: readonly SessionFilterOption[];
  datasetOptions: readonly SessionFilterOption[];
  // 칩이 id 대신 이름을 표시하기 위한 맵.
  memberNameById: Readonly<Record<string, string>>;
  experimentNameById: Readonly<Record<string, string>>;
  datasetNameByUuid: Readonly<Record<string, string>>;
  membersStatus: ListViewStatus;
  experimentsStatus: ListViewStatus;
};

/**
 * 세션 필터의 선택지: 멤버, 실험, 브랜치, 데이터셋.
 *
 * Me 는 actor 가 선점한 workspace 합집합, Team 은 선택 그룹과 멤버의 workspace 합집합이다. BE 가
 * 요청 하나에서 해석한다. FE 는 workspace 목록을 먼저 받거나 workspace 수만큼 요청을 보내지 않는다.
 *
 * 브랜치는 제출 옵션 카탈로그를, 데이터셋은 registry 카탈로그 첫 페이지를 쓴다. 브랜치는 고른 이름이
 * 그대로 서버로 보내는 검색어다. 그러나 **데이터셋은 uuid 를 보낸다.** 상류가 비교하는 값은 registry
 * 표시명이 아니라 등록 이름이라서 BE 가 uuid 를 그 이름으로 바꿔야 한다. 표시명으로 보내면 같은
 * 이름의 데이터셋을 구분할 수 없다. 그래서 칩 문구용 이름 맵을 따로 반환한다(멤버·실험과 같은 방식).
 */
export function useSessionFilterOptions(
  scope: "me" | "team",
  groupUuid: string | null,
  memberUuid: string | null,
): SessionFilterOptions {
  const members = useTeamMembers(scope === "team", groupUuid);
  // 팀 스코프는 그룹이 정해지기 전에는 조회할 범위가 없으므로 요청을 보내지 않는다.
  const experimentsFetcher = useCallback((): Promise<readonly TrainingExperiment[]> => {
    if (scope === "team" && groupUuid === null) {
      return Promise.resolve([]);
    }
    return scope === "me"
      ? listMyExperiments()
      : listTeamExperiments(memberUuid ?? undefined, groupUuid ?? undefined);
  }, [scope, groupUuid, memberUuid]);
  const experiments = useAsyncList<TrainingExperiment>(experimentsFetcher);

  const branchesFetcher = useCallback(async () => {
    const options = await getTrainingSubmitOptions();
    return options.branches;
  }, []);
  const branches = useAsyncList(branchesFetcher);

  const datasetsFetcher = useCallback(async () => {
    const page = await listDatasets({ page: 1, size: DATASET_OPTION_PAGE_SIZE });
    return page.items;
  }, []);
  const datasets = useAsyncList(datasetsFetcher);

  // 데이터셋별 학습 수. 카탈로그와 별개 축이라 따로 받는다(registry 저널 집계라 비용이 낮다).
  // 실패해도 옵션은 표시되므로 오류를 무시한다. 개수는 고를 때 참고하는 보조 정보다.
  const usageFetcher = useCallback((): Promise<readonly TrainingDatasetUsage[]> => {
    if (scope === "team" && groupUuid === null) {
      return Promise.resolve([]);
    }
    return scope === "me"
      ? listMyDatasetUsage()
      : listTeamDatasetUsage(groupUuid ?? undefined, memberUuid ?? undefined);
  }, [scope, groupUuid, memberUuid]);
  const datasetUsage = useAsyncList<TrainingDatasetUsage>(usageFetcher);
  const usageByUuid = useMemo(
    () => new Map(datasetUsage.items.map((row) => [row.dataset_uuid, row.session_count])),
    [datasetUsage.items],
  );

  return useMemo(
    () => ({
      memberOptions: members.items.map((member) => ({
        value: member.uuid,
        label: member.display_name,
      })),
      experimentOptions: experiments.items.map((experiment) => ({
        value: experiment.id,
        label: experiment.name,
      })),
      // 검색어가 이름이든 해시든 같은 항목과 일치하도록 sha 를 설명으로 함께 넣는다. 고른 값은
      // 항상 브랜치 이름이다. 카탈로그의 sha 는 그 브랜치의 현재 head 라서, sha 로 좁히면 같은
      // 브랜치의 이전 세션이 빠진다.
      branchOptions: branches.items.map((branch) => ({
        value: branch.name,
        label: branch.name,
        description: branch.sha.slice(0, 7),
      })),
      // 개수는 라벨이 아니라 설명 칸에 둔다. 이름 옆에 붙이면 잘림이 개수를 먼저 자른다.
      // 한 번도 쓰지 않은 데이터셋은 응답에 행이 없으므로 0 으로 읽는다.
      datasetOptions: datasets.items.map((dataset) => ({
        value: dataset.uuid,
        label: dataset.display_name,
        description: `Training ${usageByUuid.get(dataset.uuid) ?? 0}건`,
      })),
      memberNameById: Object.fromEntries(
        members.items.map((member) => [member.uuid, member.display_name]),
      ),
      experimentNameById: Object.fromEntries(
        experiments.items.map((experiment) => [experiment.id, experiment.name]),
      ),
      datasetNameByUuid: Object.fromEntries(
        datasets.items.map((dataset) => [dataset.uuid, dataset.display_name]),
      ),
      membersStatus: members.status,
      experimentsStatus: experiments.status,
    }),
    [
      members.items,
      members.status,
      experiments.items,
      experiments.status,
      branches.items,
      datasets.items,
      usageByUuid,
    ],
  );
}
